import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Search,
  Edit,
  Trash2,
  Eye,
  Filter,
  TrendingUp,
  BarChart3,
  Target,
  AlertTriangle,
  Star,
  RefreshCw,
  Download,
  FileText,
  Library,
  Upload,
  Leaf,
  Zap,
  Globe,
  Shield,
  Plus
} from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/use-toast';
import { unifiedLibraryService } from '@/services/unifiedLibraryService';
import { dataSyncEvents, useDataSync } from '@/utils/dataSyncEvents';
import { UserMetricData } from '@/types/unifiedLibrary';
import { DataTaggingComponent } from '@/components/DataManagement/DataTaggingComponent';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { supabase } from '@/integrations/supabase/client';
import { enhancedOrganizationalDataService } from '@/services/enhancedOrganizationalDataService';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

interface OrganizationMetricsManagerProps {
  onTabChange?: (tab: string) => void;
}

export const OrganizationMetricsManager = ({ onTabChange }: OrganizationMetricsManagerProps) => {
  const [userMetrics, setUserMetrics] = useState<UserMetricData[]>([]);
  const [filteredMetrics, setFilteredMetrics] = useState<UserMetricData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterScope, setFilterScope] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState<UserMetricData | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState<UserMetricData | null>(null);
  const [editFormData, setEditFormData] = useState({
    custom_value: '',
    unit: '',
    notes: ''
  });
  const [editSelectedTags, setEditSelectedTags] = useState<string[]>([]);
  const [viewSelectedTags, setViewSelectedTags] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { user } = useAuth();

  // ── KEY CHANGE: use organizationId everywhere instead of user.id ────────────
  // For admins: organizationId === user.id (they ARE the org)
  // For team members: organizationId === admin's user.id (their org's identity)
  const { organizationId, isOrganization, loading: orgLoading } = useOrganization();

  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();

  useEffect(() => {
    // Wait for both user and organizationId to be resolved before loading
    if (user && organizationId && !orgLoading && !dataLoaded) {
      loadData();
    }
  }, [user, organizationId, orgLoading, dataLoaded]);

  // Subscribe to data changes using organizationId (not user.id)
  // This means team members also react to changes made by other org members
  useDataSync(organizationId || '', () => {
    if (user && organizationId) {
      loadData();
    }
  });

  useEffect(() => {
    applyFiltersAndSearch();
  }, [userMetrics, searchTerm, filterCategory, filterScope]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (!user || !organizationId) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to access your metrics data',
          variant: 'destructive'
        });
        setUserMetrics([]);
        setCategories([]);
        setDataLoaded(false);
        return;
      }

      // ── Use organizationId so team members see the org's metrics ─────────────
      const { data: metrics, error: metricsError } = await supabase
        .from('user_metrics_data')
        .select('*')
        .eq('organization_id', organizationId)   // ← was user.id
        .order('created_at', { ascending: false });

      if (metricsError) throw metricsError;

      const transformedMetrics: UserMetricData[] = (metrics as any || []).map(metric => ({
        id: metric.id,
        organization_id: metric.organization_id,
        metric_id: metric.metric_id,
        name: metric.name,
        category: metric.category,
        subcategory: metric.subcategory,
        description: metric.description,
        unit: metric.unit,
        currency: metric.currency,
        custom_value: metric.custom_value,
        custom_metadata: metric.custom_metadata,
        is_custom: metric.is_custom,
        created_at: metric.created_at,
        updated_at: metric.updated_at
      }));

      const categoriesData = await unifiedLibraryService.getMetricCategories();

      setUserMetrics(transformedMetrics);
      setCategories(categoriesData);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading organization metrics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organization metrics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...userMetrics];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const searchableFields = [
          item.name,
          item.category,
          item.subcategory,
          item.description,
          item.unit,
          item.currency,
          item.custom_value?.toString(),
          item.custom_metadata?.data_source,
          item.custom_metadata?.notes
        ].filter(Boolean);
        return searchableFields.some(field => field.toLowerCase().includes(term));
      });
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(item => item.category === filterCategory);
    }

    if (filterScope !== 'all') {
      filtered = filtered.filter(item => item.custom_metadata?.scope === filterScope);
    }

    setFilteredMetrics(filtered);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);
  const clearFilters = () => { setSearchTerm(''); setFilterCategory('all'); setFilterScope('all'); };

  // ─── EDIT ────────────────────────────────────────────────────────────────────
  const handleEdit = async (metric: UserMetricData) => {
    setSelectedMetric(metric);
    setEditFormData({
      custom_value: metric.custom_value?.toString() || '',
      unit: metric.unit || '',
      notes: metric.custom_metadata?.notes || ''
    });

    try {
      const { tagService } = await import('@/services/tagService');
      // ── Use organizationId for tag lookup ──────────────────────────────────
      const taggedData = await tagService.getTaggedData(organizationId, 'metric');
      const metricTags = taggedData.find(td => td.data_id === metric.id);
      setEditSelectedTags(metricTags?.tag_ids || []);
    } catch (error) {
      console.warn('Failed to load tags for metric:', error);
      setEditSelectedTags([]);
    }

    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedMetric || !user || !organizationId) return;

    try {
      // ── Use organizationId for the update filter ───────────────────────────
      const { error } = await supabase
        .from('user_metrics_data')
        .update({
          custom_value: parseFloat(editFormData.custom_value) || undefined,
          unit: editFormData.unit || selectedMetric.unit,
          custom_metadata: {
            ...selectedMetric.custom_metadata,
            notes: editFormData.notes || null
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedMetric.id)
        .eq('organization_id', organizationId);   // ← was user.id

      if (error) throw error;

      try {
        const { tagService } = await import('@/services/tagService');
        // ── Use organizationId for tag updates ─────────────────────────────
        await tagService.tagData(organizationId, 'metric', selectedMetric.id, editSelectedTags);
      } catch (tagError) {
        console.warn('Failed to update tags for metric:', tagError);
      }

      // ── Emit with organizationId so all org members get the sync event ────
      dataSyncEvents.emitMetricChange('updated', selectedMetric.id, organizationId);

      const updatedMetrics = userMetrics.map(item =>
        item.id === selectedMetric.id
          ? {
              ...item,
              custom_value: parseFloat(editFormData.custom_value) || item.custom_value,
              unit: editFormData.unit || item.unit,
              custom_metadata: { ...item.custom_metadata, notes: editFormData.notes || null },
              updated_at: new Date().toISOString()
            }
          : item
      );
      setUserMetrics(updatedMetrics);

      toast({ title: 'Success', description: 'Metric updated successfully' });
      setIsEditModalOpen(false);
      setSelectedMetric(null);
    } catch (error) {
      console.error('Error updating metric:', error);
      toast({ title: 'Error', description: 'Failed to update metric', variant: 'destructive' });
    }
  };

  // ─── DELETE ──────────────────────────────────────────────────────────────────
  const handleDelete = (metric: UserMetricData) => {
    setMetricToDelete(metric);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!metricToDelete || !user || !organizationId) return;

    try {
      // ── Use organizationId for the delete filter ───────────────────────────
      const { error } = await supabase
        .from('user_metrics_data')
        .delete()
        .eq('id', metricToDelete.id)
        .eq('organization_id', organizationId);   // ← was user.id

      if (error) throw error;

      // ── Emit with organizationId ───────────────────────────────────────────
      dataSyncEvents.emitMetricChange('deleted', metricToDelete.id, organizationId);

      setUserMetrics(userMetrics.filter(item => item.id !== metricToDelete.id));
      toast({ title: 'Success', description: 'Metric deleted successfully' });
      setIsDeleteDialogOpen(false);
      setMetricToDelete(null);
    } catch (error) {
      console.error('Error deleting metric:', error);
      toast({ title: 'Error', description: 'Failed to delete metric', variant: 'destructive' });
      setIsDeleteDialogOpen(false);
      setMetricToDelete(null);
    }
  };

  // ─── VIEW ────────────────────────────────────────────────────────────────────
  const handleView = async (metric: UserMetricData) => {
    setSelectedMetric(metric);
    try {
      const { tagService } = await import('@/services/tagService');
      // ── Use organizationId for tag lookup ──────────────────────────────────
      const taggedData = await tagService.getTaggedData(organizationId, 'metric');
      const metricTags = taggedData.find(td => td.data_id === metric.id);
      setViewSelectedTags(metricTags?.tag_ids || []);
    } catch (error) {
      console.warn('Failed to load tags for metric:', error);
      setViewSelectedTags([]);
    }
    setIsViewModalOpen(true);
  };

  // ─── REFRESH ─────────────────────────────────────────────────────────────────
  const handleManualRefresh = async () => {
    if (user && organizationId) {
      setDataLoaded(false);
      // ── Use organizationId for the refresh ────────────────────────────────
      await enhancedOrganizationalDataService.refreshUserData(organizationId, 'metrics');
      await loadData();
      toast({ title: 'Success', description: 'Metrics data refreshed successfully' });
    }
  };

  // ─── HELPERS ─────────────────────────────────────────────────────────────────
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Environmental': return <Leaf className="h-4 w-4" />;
      case 'Social': return <Globe className="h-4 w-4" />;
      case 'Governance': return <Shield className="h-4 w-4" />;
      case 'Economic': return <TrendingUp className="h-4 w-4" />;
      case 'Carbon Emissions': return <Zap className="h-4 w-4" />;
      case 'Energy': return <BarChart3 className="h-4 w-4" />;
      case 'Waste': return <AlertTriangle className="h-4 w-4" />;
      case 'Water': return <Target className="h-4 w-4" />;
      default: return <BarChart3 className="h-4 w-4" />;
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'Scope 1': return 'bg-red-100 text-red-800';
      case 'Scope 2': return 'bg-orange-100 text-orange-800';
      case 'Scope 3': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // ─── EXPORTS (unchanged logic, just user metadata for display) ───────────────
  const handleExportCSV = async () => {
    if (!(await guardDownload())) return;

    try {
      const headers = ['Name','Category','Subcategory','Value','Unit','Scope','Status','Data Source','Reporting Period','Description','Notes','Created At','Updated At'];
      const csvContent = [
        headers.join(','),
        ...filteredMetrics.map(metric => [
          `"${metric.name||''}"`,`"${metric.category||''}"`,`"${metric.subcategory||''}"`,
          `"${metric.custom_value||''}"`,`"${metric.unit||''}"`,`"${metric.custom_metadata?.scope||''}"`,
          `"${metric.custom_metadata?.validation_status||''}"`,`"${metric.custom_metadata?.data_source||''}"`,
          `"${metric.custom_metadata?.reporting_period||''}"`,`"${metric.description||''}"`,
          `"${metric.custom_metadata?.notes||''}"`,`"${metric.created_at||''}"`,`"${metric.updated_at||''}"`
        ].join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `organization_metrics_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: 'Export Successful', description: 'Metrics exported to CSV.' });
    } catch (error) {
      toast({ title: 'Export Error', description: 'Failed to export CSV.', variant: 'destructive' });
    }
  };

  const generateAIInsights = () => {
    if (filteredMetrics.length === 0) return "No metrics data available for analysis.";
    const insights = [];
    const totalMetrics = filteredMetrics.length;
    const cats = [...new Set(filteredMetrics.map(m => m.category).filter(Boolean))];
    const scopes = [...new Set(filteredMetrics.map(m => m.custom_metadata?.scope).filter(Boolean))];
    const statuses = [...new Set(filteredMetrics.map(m => m.custom_metadata?.validation_status).filter(Boolean))];
    insights.push(`<h3>Dataset Overview</h3><p>This report contains <strong>${totalMetrics}</strong> ESG metrics across <strong>${cats.length}</strong> categories spanning <strong>${scopes.length}</strong> emission scopes.</p>`);
    if (cats.length > 0) {
      const cc = cats.map(c => ({ category: c, count: filteredMetrics.filter(m => m.category === c).length })).sort((a,b)=>b.count-a.count);
      insights.push(`<h3>Category Distribution</h3><p>Most represented: <strong>${cc[0].category}</strong> (${cc[0].count} metrics, ${Math.round(cc[0].count/totalMetrics*100)}%).</p>`);
    }
    let validationRate = 0;
    if (statuses.length > 0) {
      const sc = statuses.map(s => ({ status: s, count: filteredMetrics.filter(m => m.custom_metadata?.validation_status === s).length })).sort((a,b)=>b.count-a.count);
      const validatedCount = sc.find(s => s.status === 'validated')?.count || 0;
      validationRate = Math.round(validatedCount / totalMetrics * 100);
      insights.push(`<h3>Data Quality</h3><p>${validationRate}% of metrics validated — ${validationRate>=70?'strong':validationRate>=50?'moderate':'needs improvement'}.</p>`);
    }
    insights.push(`<h3>Recommendations</h3><ul>${scopes.length<3?'<li>Expand scope coverage for comprehensive reporting.</li>':''}${validationRate<80?'<li>Validate remaining metrics to improve data reliability.</li>':''}<li>Establish periodic reviews to align with organizational goals.</li></ul>`);
    return insights.join('');
  };

  const handleExportHTML = async () => {
    if (!(await guardDownload())) return;

    try {
      const htmlContent = buildReportHTML(generateAIInsights());
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `organization_metrics_report_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast({ title: 'Export Successful', description: 'HTML report exported.' });
    } catch (error) {
      toast({ title: 'Export Error', description: 'Failed to export HTML.', variant: 'destructive' });
    }
  };

  const buildReportHTML = (insights: string) => `
    <!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>ESG Metrics Report</title>
    <style>
      body{font-family:'Segoe UI',sans-serif;margin:20px;line-height:1.6;color:#333}
      .header{text-align:center;padding:20px;background:linear-gradient(135deg,#16a34a,#15803d);color:white;border-radius:10px;margin-bottom:30px}
      table{width:100%;border-collapse:collapse;margin:20px 0;font-size:12px}
      th,td{border:1px solid #e0e0e0;padding:10px 8px;text-align:left;vertical-align:top}
      th{background:linear-gradient(135deg,#16a34a,#15803d);color:white;font-size:11px;text-transform:uppercase}
      tr:nth-child(even){background:#f8f9fa}
      .insights{padding:20px;border:1px solid #e0e0e0;border-radius:8px;margin-top:30px}
      .insights h3{color:#16a34a;border-bottom:2px solid #16a34a;padding-bottom:5px}
      .footer{text-align:center;padding:20px;background:#f8f9fa;border-radius:8px;margin-top:30px;font-size:12px;color:#666}
    </style></head><body>
    <div class="header">
      <h1>Organization ESG Metrics Report</h1>
      <p>Generated: ${new Date().toLocaleString()} · Total: ${filteredMetrics.length} metrics</p>
      <p>Generated by: ${user?.user_metadata?.full_name || user?.email || 'N/A'}</p>
    </div>
    <h2 style="color:#16a34a">Metrics</h2>
    <table><thead><tr><th>Name</th><th>Category</th><th>Subcategory</th><th>Value</th><th>Unit</th><th>Scope</th><th>Status</th><th>Source</th><th>Period</th><th>Notes</th></tr></thead>
    <tbody>${filteredMetrics.map(m=>`<tr>
      <td><strong>${m.name||'N/A'}</strong></td><td>${m.category||'N/A'}</td><td>${m.subcategory||'N/A'}</td>
      <td><strong>${m.custom_value||'N/A'}</strong></td><td>${m.unit||'N/A'}</td>
      <td>${m.custom_metadata?.scope||'N/A'}</td><td>${m.custom_metadata?.validation_status||'N/A'}</td>
      <td>${m.custom_metadata?.data_source||'N/A'}</td><td>${m.custom_metadata?.reporting_period||'N/A'}</td>
      <td>${m.custom_metadata?.notes||'N/A'}</td>
    </tr>`).join('')}</tbody></table>
    <div class="insights"><h2 style="color:#16a34a">AI-Generated Insights</h2>${insights}</div>
    <div class="footer"><p>GreenCard Sustainability Manager</p></div>
    </body></html>`;

  const handleExportPDF = async () => {
    if (!(await guardDownload())) return;

    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position:absolute;left:-9999px;top:0;width:1200px;background:white;padding:20px';
      tempDiv.innerHTML = buildReportHTML(generateAIInsights());
      document.body.appendChild(tempDiv);
      const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: 1200, height: tempDiv.scrollHeight, logging: false });
      document.body.removeChild(tempDiv);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`organization_metrics_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast({ title: 'Export Successful', description: 'PDF report exported.' });
    } catch (error) {
      toast({ title: 'Export Error', description: 'Failed to export PDF.', variant: 'destructive' });
    }
  };

  // ─── LOADING STATES ──────────────────────────────────────────────────────────
  if (loading || orgLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading organization metrics...</span>
        </div>
      </div>
    );
  }

  if (userMetrics.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" />Organization ESG Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription className="text-center py-8">
                <div className="flex flex-col items-center gap-4">
                  <BarChart3 className="h-12 w-12 text-gray-400" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No ESG Metrics Added Yet</h3>
                    <p className="text-gray-600 mb-4">Start by adding metrics through the Quick Entry tab or importing from the Metrics Library.</p>
                    <div className="flex gap-2 justify-center">
                      <Button onClick={() => onTabChange?.('quick-entry')}><Upload className="h-4 w-4 mr-2" />Add Data</Button>
                      <Button variant="outline" onClick={() => onTabChange?.('metrics')}><Library className="h-4 w-4 mr-2" />Browse Metrics</Button>
                    </div>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── MAIN RENDER ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Organization ESG Metrics ({filteredMetrics.length} items)</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" />CSV</Button>
              <Button variant="outline" size="sm" onClick={handleExportHTML}><FileText className="w-4 h-4 mr-2" />HTML</Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF}><FileText className="w-4 h-4 mr-2" />PDF</Button>
            </div>
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input placeholder="Search metrics..." value={searchTerm} onChange={handleSearchChange} className="pl-10" />
            </div>
            <div className="flex gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterScope} onValueChange={setFilterScope}>
                <SelectTrigger className="w-[120px]"><SelectValue placeholder="Scope" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scopes</SelectItem>
                  <SelectItem value="Scope 1">Scope 1</SelectItem>
                  <SelectItem value="Scope 2">Scope 2</SelectItem>
                  <SelectItem value="Scope 3">Scope 3</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={clearFilters}><Filter className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMetrics.map((metric) => (
                  <TableRow key={metric.id}>
                    <TableCell>
                      <div className="font-medium">{metric.name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{metric.subcategory || 'N/A'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(metric.category)}
                        <Badge variant="outline">{metric.category || 'N/A'}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>{metric.custom_value || 'N/A'}</TableCell>
                    <TableCell>{metric.unit || 'N/A'}</TableCell>
                    <TableCell>
                      {metric.custom_metadata?.scope
                        ? <Badge className={getScopeColor(metric.custom_metadata.scope)}>{metric.custom_metadata.scope}</Badge>
                        : <span className="text-gray-400 text-sm">N/A</span>}
                    </TableCell>
                    <TableCell>
                      {metric.custom_metadata?.validation_status
                        ? <Badge className={getStatusColor(metric.custom_metadata.validation_status)}>{metric.custom_metadata.validation_status}</Badge>
                        : <span className="text-gray-400 text-sm">N/A</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleView(metric)}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(metric)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(metric)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Metric</DialogTitle>
            <DialogDescription>Update the metric values and metadata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="custom_value">Value</Label>
              <Input id="custom_value" type="number" step="0.01" value={editFormData.custom_value} onChange={(e) => setEditFormData(prev => ({ ...prev, custom_value: e.target.value }))} placeholder="Enter metric value" />
            </div>
            <div>
              <Label htmlFor="unit">Unit</Label>
              <Input id="unit" value={editFormData.unit} onChange={(e) => setEditFormData(prev => ({ ...prev, unit: e.target.value }))} placeholder="e.g., kg CO2e, kWh" />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" value={editFormData.notes} onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))} placeholder="Additional notes or context" rows={3} />
            </div>
            <div>
              <Label className="text-sm font-medium">Tags</Label>
              <p className="text-xs text-gray-600 mb-2">Manage tags to organize and categorize this metric</p>
              <DataTaggingComponent selectedTags={editSelectedTags} onTagsChange={setEditSelectedTags} dataType="metric" showCreateTag={true} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveEdit}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedMetric && getCategoryIcon(selectedMetric.category)}
              {selectedMetric?.name || 'Metric Details'}
            </DialogTitle>
            <DialogDescription>Comprehensive view of all metric information and metadata</DialogDescription>
          </DialogHeader>
          {selectedMetric && (
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label className="text-sm font-medium text-gray-700">Metric Name</Label><p className="text-sm font-medium">{selectedMetric.name || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Category</Label><div className="flex items-center gap-2">{getCategoryIcon(selectedMetric.category)}<Badge variant="outline">{selectedMetric.category || 'N/A'}</Badge></div></div>
                  <div><Label className="text-sm font-medium text-gray-700">Subcategory</Label><p className="text-sm">{selectedMetric.subcategory || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Value</Label><p className="text-sm font-semibold">{selectedMetric.custom_value || 'N/A'} {selectedMetric.unit || ''}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Unit</Label><p className="text-sm">{selectedMetric.unit || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Description</Label><p className="text-sm">{selectedMetric.description || 'N/A'}</p></div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-blue-800">ESG Classification</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label className="text-sm font-medium text-blue-700">Emission Scope</Label>{selectedMetric.custom_metadata?.scope ? <Badge className={getScopeColor(selectedMetric.custom_metadata.scope)}>{selectedMetric.custom_metadata.scope}</Badge> : <p className="text-sm text-gray-500">N/A</p>}</div>
                  <div><Label className="text-sm font-medium text-blue-700">Validation Status</Label>{selectedMetric.custom_metadata?.validation_status ? <Badge className={getStatusColor(selectedMetric.custom_metadata.validation_status)}>{selectedMetric.custom_metadata.validation_status}</Badge> : <p className="text-sm text-gray-500">N/A</p>}</div>
                  <div><Label className="text-sm font-medium text-blue-700">Data Source</Label><p className="text-sm">{selectedMetric.custom_metadata?.data_source || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-blue-700">Reporting Period</Label><p className="text-sm">{selectedMetric.custom_metadata?.reporting_period || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-blue-700">Calculation Method</Label><p className="text-sm">{selectedMetric.custom_metadata?.calculation_method || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-blue-700">Verification Status</Label><p className="text-sm">{selectedMetric.custom_metadata?.verification_status || 'N/A'}</p></div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-green-800">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-sm font-medium text-green-700">Notes</Label><p className="text-sm whitespace-pre-wrap">{selectedMetric.custom_metadata?.notes || 'No notes available'}</p></div>
                  <div><Label className="text-sm font-medium text-green-700">Tags</Label>
                    <div className="flex flex-wrap gap-1">
                      {viewSelectedTags?.length > 0
                        ? viewSelectedTags.map((tagId, i) => <Badge key={i} variant="secondary" className="text-xs">{tagId}</Badge>)
                        : <p className="text-sm text-gray-500">No tags assigned</p>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">System Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label className="text-sm font-medium text-gray-700">Metric ID</Label><p className="text-sm font-mono">{selectedMetric.id || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Library ID</Label><p className="text-sm font-mono">{selectedMetric.metric_id || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Organization ID</Label><p className="text-sm font-mono">{selectedMetric.organization_id || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Created At</Label><p className="text-sm">{selectedMetric.created_at ? new Date(selectedMetric.created_at).toLocaleString() : 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Updated At</Label><p className="text-sm">{selectedMetric.updated_at ? new Date(selectedMetric.updated_at).toLocaleString() : 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Sync Status</Label><Badge variant={selectedMetric.custom_metadata?.sync_status === 'synced' ? 'default' : 'secondary'}>{selectedMetric.custom_metadata?.sync_status || 'synced'}</Badge></div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{metricToDelete?.name}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setIsDeleteDialogOpen(false); setMetricToDelete(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};






// import React, { useState, useEffect } from 'react';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Badge } from '@/components/ui/badge';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
// import { Textarea } from '@/components/ui/textarea';
// import { Label } from '@/components/ui/label';
// import {
//   Search,
//   Edit,
//   Trash2,
//   Eye,
//   Filter,
//   TrendingUp,
//   BarChart3,
//   Target,
//   AlertTriangle,
//   Star,
//   MoreHorizontal,
//   RefreshCw,
//   Download,
//   FileText,
//   Library,
//   Upload,
//   Leaf,
//   Zap,
//   Globe,
//   Shield,
//   Plus
// } from 'lucide-react';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { useToast } from '@/hooks/use-toast';
// import { unifiedLibraryService } from '@/services/unifiedLibraryService';
// import { quickEntryService } from '@/services/quickEntryService';
// import { dataSyncEvents, useDataSync } from '@/utils/dataSyncEvents';
// import { UserMetricData } from '@/types/unifiedLibrary';
// import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
// import { DataTaggingComponent } from '@/components/DataManagement/DataTaggingComponent';
// import html2canvas from 'html2canvas';
// import jsPDF from 'jspdf';
// import { supabase } from '@/integrations/supabase/client';
// import { enhancedOrganizationalDataService } from '@/services/enhancedOrganizationalDataService';

// interface OrganizationMetricsManagerProps {
//   onTabChange?: (tab: string) => void;
// }

// export const OrganizationMetricsManager = ({ onTabChange }: OrganizationMetricsManagerProps) => {
//   const [userMetrics, setUserMetrics] = useState<UserMetricData[]>([]);
//   const [filteredMetrics, setFilteredMetrics] = useState<UserMetricData[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [filterCategory, setFilterCategory] = useState('all');
//   const [filterScope, setFilterScope] = useState('all');
//   const [selectedMetric, setSelectedMetric] = useState<UserMetricData | null>(null);
//   const [isViewModalOpen, setIsViewModalOpen] = useState(false);
//   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
//   const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
//   const [metricToDelete, setMetricToDelete] = useState<UserMetricData | null>(null);
//   const [editFormData, setEditFormData] = useState({
//     custom_value: '',
//     unit: '',
//     notes: ''
//   });
//   const [editSelectedTags, setEditSelectedTags] = useState<string[]>([]);
//   const [viewSelectedTags, setViewSelectedTags] = useState<string[]>([]);
//   const [categories, setCategories] = useState<string[]>([]);
//   const [dataLoaded, setDataLoaded] = useState(false);
//   const { user } = useAuth();
//   const { toast } = useToast();

//   useEffect(() => {
//     if (user && !dataLoaded) {
//       loadData();
//     }
//   }, [user, dataLoaded]);

//   // Subscribe to data changes from other components
//   useDataSync(user?.id || '', () => {
//     if (user) {
//       loadData();
//     }
//   });

//   useEffect(() => {
//     applyFiltersAndSearch();
//   }, [userMetrics, searchTerm, filterCategory, filterScope]);

//   const loadData = async () => {
//     try {
//       setLoading(true);

//       if (!user) {
//         toast({
//           title: 'Authentication Required',
//           description: 'Please log in to access your metrics data',
//           variant: 'destructive'
//         });
//         setUserMetrics([]);
//         setCategories([]);
//         setDataLoaded(false);
//         return;
//       }

//       // Load user metrics from Supabase
//       const { data: metrics, error: metricsError } = await supabase
//         .from('user_metrics_data')
//         .select('*')
//         .eq('organization_id', user.id)
//         .order('created_at', { ascending: false });

//       if (metricsError) {
//         throw metricsError;
//       }

//       // Transform to UserMetricData format
//       const transformedMetrics: UserMetricData[] = (metrics as any || []).map(metric => ({
//         id: metric.id,
//         organization_id: metric.organization_id,
//         metric_id: metric.metric_id,
//         name: metric.name,
//         category: metric.category,
//         subcategory: metric.subcategory,
//         description: metric.description,
//         unit: metric.unit,
//         currency: metric.currency,
//         custom_value: metric.custom_value,
//         custom_metadata: metric.custom_metadata,
//         is_custom: metric.is_custom,
//         created_at: metric.created_at,
//         updated_at: metric.updated_at
//       }));

//       // Load categories from the library service
//       const categoriesData = await unifiedLibraryService.getMetricCategories();

//       setUserMetrics(transformedMetrics);
//       setCategories(categoriesData);
//       setDataLoaded(true);
//     } catch (error) {
//       console.error('Error loading user metrics:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to load organization metrics',
//         variant: 'destructive'
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const saveMetricsToStorage = async (metrics: UserMetricData[]): Promise<boolean> => {
//     try {
//       // Note: This function may not be needed anymore since we're directly updating Supabase
//       // But keeping it for compatibility with existing code
//       console.log('saveMetricsToStorage called - now using Supabase directly');
//       return true;
//     } catch (error) {
//       console.error('Error saving metrics:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to save changes',
//         variant: 'destructive'
//       });
//       return false;
//     }
//   };

//   const applyFiltersAndSearch = () => {
//     let filtered = [...userMetrics];

//     // Apply search filter
//     if (searchTerm.trim()) {
//       const term = searchTerm.toLowerCase().trim();
//       filtered = filtered.filter(item => {
//         const searchableFields = [
//           item.name,
//           item.category,
//           item.subcategory,
//           item.description,
//           item.unit,
//           item.currency,
//           item.custom_value?.toString(),
//           item.custom_metadata?.data_source,
//           item.custom_metadata?.notes
//         ].filter(Boolean);

//         return searchableFields.some(field =>
//           field.toLowerCase().includes(term)
//         );
//       });
//     }

//     // Apply category filter
//     if (filterCategory !== 'all') {
//       filtered = filtered.filter(item => item.category === filterCategory);
//     }

//     // Apply scope filter
//     if (filterScope !== 'all') {
//       filtered = filtered.filter(item =>
//         item.custom_metadata?.scope === filterScope
//       );
//     }

//     setFilteredMetrics(filtered);
//   };

//   const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     setSearchTerm(e.target.value);
//   };

//   const clearFilters = () => {
//     setSearchTerm('');
//     setFilterCategory('all');
//     setFilterScope('all');
//   };

//   const handleEdit = async (metric: UserMetricData) => {
//     setSelectedMetric(metric);
//     setEditFormData({
//       custom_value: metric.custom_value?.toString() || '',
//       unit: metric.unit || '',
//       notes: metric.custom_metadata?.notes || ''
//     });

//     // Load existing tags for this metric
//     try {
//       const { tagService } = await import('@/services/tagService');
//       const taggedData = await tagService.getTaggedData(user.id, 'metric');
//       const metricTags = taggedData.find(td => td.data_id === metric.id);
//       setEditSelectedTags(metricTags?.tag_ids || []);
//     } catch (error) {
//       console.warn('Failed to load tags for metric:', error);
//       setEditSelectedTags([]);
//     }

//     setIsEditModalOpen(true);
//   };

//   const handleSaveEdit = async () => {
//     if (!selectedMetric || !user) return;

//     try {
//       // Update the metric in Supabase
//       const { error } = await supabase
//         .from('user_metrics_data')
//         .update({
//           custom_value: parseFloat(editFormData.custom_value) || undefined,
//           unit: editFormData.unit || selectedMetric.unit,
//           custom_metadata: {
//             ...selectedMetric.custom_metadata,
//             notes: editFormData.notes || null
//           },
//           updated_at: new Date().toISOString()
//         })
//         .eq('id', selectedMetric.id)
//         .eq('organization_id', user.id);

//       if (error) {
//         throw error;
//       }

//       // Update tags for the metric
//       try {
//         const { tagService } = await import('@/services/tagService');
//         await tagService.tagData(user.id, 'metric', selectedMetric.id, editSelectedTags);
//       } catch (tagError) {
//         console.warn('Failed to update tags for metric:', tagError);
//         // Don't fail the entire operation if tagging fails
//       }

//       // Emit event to notify other components
//       dataSyncEvents.emitMetricChange('updated', selectedMetric.id, user.id);

//       // Update local state
//       const updatedMetrics = userMetrics.map(item =>
//         item.id === selectedMetric.id
//           ? {
//             ...item,
//             custom_value: parseFloat(editFormData.custom_value) || item.custom_value,
//             unit: editFormData.unit || item.unit,
//             custom_metadata: {
//               ...item.custom_metadata,
//               notes: editFormData.notes || null
//             },
//             updated_at: new Date().toISOString()
//           }
//           : item
//       );
//       setUserMetrics(updatedMetrics);

//       toast({
//         title: 'Success',
//         description: 'Metric updated successfully',
//       });

//       setIsEditModalOpen(false);
//       setSelectedMetric(null);
//     } catch (error) {
//       console.error('Error updating metric:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to update metric',
//         variant: 'destructive'
//       });
//     }
//   };

//   const handleDelete = (metric: UserMetricData) => {
//     setMetricToDelete(metric);
//     setIsDeleteDialogOpen(true);
//   };

//   const confirmDelete = async () => {
//     if (!metricToDelete) return;

//     try {
//       if (!user) return;

//       // Delete from Supabase
//       const { error } = await supabase
//         .from('user_metrics_data')
//         .delete()
//         .eq('id', metricToDelete.id)
//         .eq('organization_id', user.id);

//       if (error) {
//         throw error;
//       }

//       // Emit event to notify other components
//       dataSyncEvents.emitMetricChange('deleted', metricToDelete.id, user.id);

//       // Remove from local state
//       const updatedMetrics = userMetrics.filter(item => item.id !== metricToDelete.id);
//       setUserMetrics(updatedMetrics);

//       toast({
//         title: 'Success',
//         description: 'Metric deleted successfully',
//       });

//       // Close dialog and reset state
//       setIsDeleteDialogOpen(false);
//       setMetricToDelete(null);
//     } catch (error) {
//       console.error('Error deleting metric:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to delete metric',
//         variant: 'destructive'
//       });
//       // Close dialog on error too
//       setIsDeleteDialogOpen(false);
//       setMetricToDelete(null);
//     }
//   };

//   const handleView = async (metric: UserMetricData) => {
//     setSelectedMetric(metric);

//     // Load tags for this metric
//     try {
//       const { tagService } = await import('@/services/tagService');
//       const taggedData = await tagService.getTaggedData(user.id, 'metric');
//       const metricTags = taggedData.find(td => td.data_id === metric.id);
//       setViewSelectedTags(metricTags?.tag_ids || []);
//     } catch (error) {
//       console.warn('Failed to load tags for metric:', error);
//       setViewSelectedTags([]);
//     }

//     setIsViewModalOpen(true);
//   };

//   const getCategoryIcon = (category: string) => {
//     switch (category) {
//       case 'Environmental': return <Leaf className="h-4 w-4" />;
//       case 'Social': return <Globe className="h-4 w-4" />;
//       case 'Governance': return <Shield className="h-4 w-4" />;
//       case 'Economic': return <TrendingUp className="h-4 w-4" />;
//       case 'Carbon Emissions': return <Zap className="h-4 w-4" />;
//       case 'Energy': return <BarChart3 className="h-4 w-4" />;
//       case 'Waste': return <AlertTriangle className="h-4 w-4" />;
//       case 'Water': return <Target className="h-4 w-4" />;
//       default: return <BarChart3 className="h-4 w-4" />;
//     }
//   };

//   const getScopeColor = (scope: string) => {
//     switch (scope) {
//       case 'Scope 1': return 'bg-red-100 text-red-800';
//       case 'Scope 2': return 'bg-orange-100 text-orange-800';
//       case 'Scope 3': return 'bg-blue-100 text-blue-800';
//       default: return 'bg-gray-100 text-gray-800';
//     }
//   };

//   const getStatusColor = (status: string) => {
//     switch (status) {
//       case 'approved': return 'bg-green-100 text-green-800';
//       case 'pending': return 'bg-yellow-100 text-yellow-800';
//       case 'rejected': return 'bg-red-100 text-red-800';
//       default: return 'bg-gray-100 text-gray-800';
//     }
//   };

//   const renderStars = (rating: number) => {
//     return [...Array(5)].map((_, i) => (
//       <Star
//         key={i}
//         className={`h-3 w-3 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
//           }`}
//       />
//     ));
//   };

//   const handleManualRefresh = async () => {
//     if (user) {
//       setDataLoaded(false);
//       await enhancedOrganizationalDataService.refreshUserData(user.id, 'metrics');
//       await loadData();
//       toast({
//         title: 'Success',
//         description: 'Metrics data refreshed successfully',
//       });
//     }
//   };

//   const handleExportCSV = () => {
//     try {
//       // Create CSV content
//       const headers = [
//         'Name',
//         'Category',
//         'Subcategory',
//         'Value',
//         'Unit',
//         'Scope',
//         'Status',
//         'Data Source',
//         'Reporting Period',
//         'Description',
//         'Notes',
//         'Created At',
//         'Updated At'
//       ];

//       const csvContent = [
//         headers.join(','),
//         ...filteredMetrics.map(metric => [
//           `"${metric.name || ''}"`,
//           `"${metric.category || ''}"`,
//           `"${metric.subcategory || ''}"`,
//           `"${metric.custom_value || ''}"`,
//           `"${metric.unit || ''}"`,
//           `"${metric.custom_metadata?.scope || ''}"`,
//           `"${metric.custom_metadata?.validation_status || ''}"`,
//           `"${metric.custom_metadata?.data_source || ''}"`,
//           `"${metric.custom_metadata?.reporting_period || ''}"`,
//           `"${metric.description || ''}"`,
//           `"${metric.custom_metadata?.notes || ''}"`,
//           `"${metric.created_at || ''}"`,
//           `"${metric.updated_at || ''}"`
//         ].join(','))
//       ].join('\n');

//       // Create and download file
//       const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
//       const link = document.createElement('a');
//       const url = URL.createObjectURL(blob);
//       link.setAttribute('href', url);
//       link.setAttribute('download', `organization_metrics_${new Date().toISOString().split('T')[0]}.csv`);
//       link.style.visibility = 'hidden';
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);

//       toast({
//         title: 'Export Successful',
//         description: 'Metrics data has been exported to CSV successfully.',
//       });
//     } catch (error) {
//       console.error('Error exporting CSV:', error);
//       toast({
//         title: 'Export Error',
//         description: 'Failed to export metrics data to CSV.',
//         variant: 'destructive'
//       });
//     }
//   };

//   const handleExportHTML = () => {
//     try {
//       // Generate AI insights based on the current filtered data
//       const generateAIInsights = () => {
//         if (filteredMetrics.length === 0) {
//           return "No metrics data available for analysis.";
//         }

//         const insights = [];

//         // Basic statistics
//         const totalMetrics = filteredMetrics.length;
//         const categories = [...new Set(filteredMetrics.map(m => m.category).filter(Boolean))];
//         const scopes = [...new Set(filteredMetrics.map(m => m.custom_metadata?.scope).filter(Boolean))];
//         const statuses = [...new Set(filteredMetrics.map(m => m.custom_metadata?.validation_status).filter(Boolean))];

//         insights.push(`<h3>Dataset Overview</h3>`);
//         insights.push(`<p>This report contains <strong>${totalMetrics}</strong> ESG metrics across <strong>${categories.length}</strong> categories. The data spans <strong>${scopes.length}</strong> emission scopes with <strong>${statuses.length}</strong> different validation statuses.</p>`);

//         // Category analysis
//         if (categories.length > 0) {
//           const categoryCounts = categories.map(cat => ({
//             category: cat,
//             count: filteredMetrics.filter(m => m.category === cat).length
//           })).sort((a, b) => b.count - a.count);

//           insights.push(`<h3>Category Distribution</h3>`);
//           insights.push(`<p>The most represented category is <strong>${categoryCounts[0].category}</strong> with ${categoryCounts[0].count} metrics (${Math.round(categoryCounts[0].count / totalMetrics * 100)}% of total).`);

//           if (categoryCounts.length > 1) {
//             insights.push(` Other significant categories include <strong>${categoryCounts[1].category}</strong> (${categoryCounts[1].count} metrics) and <strong>${categoryCounts[2]?.category || 'N/A'}</strong> (${categoryCounts[2]?.count || 0} metrics).</p>`);
//           } else {
//             insights.push(`</p>`);
//           }
//         }

//         // Scope analysis
//         if (scopes.length > 0) {
//           const scopeCounts = scopes.map(scope => ({
//             scope: scope,
//             count: filteredMetrics.filter(m => m.custom_metadata?.scope === scope).length
//           })).sort((a, b) => b.count - a.count);

//           insights.push(`<h3>Emission Scope Analysis</h3>`);
//           insights.push(`<p>Scope coverage analysis shows <strong>${scopeCounts[0].scope}</strong> as the most tracked scope with ${scopeCounts[0].count} metrics.`);

//           if (scopeCounts.length > 1) {
//             insights.push(` This is followed by <strong>${scopeCounts[1].scope}</strong> (${scopeCounts[1].count} metrics) and <strong>${scopeCounts[2]?.scope || 'N/A'}</strong> (${scopeCounts[2]?.count || 0} metrics).</p>`);
//           } else {
//             insights.push(`</p>`);
//           }
//         }

//         // Validation status analysis
//         let validationRate = 0; // Declare outside the if block
//         if (statuses.length > 0) {
//           const statusCounts = statuses.map(status => ({
//             status: status,
//             count: filteredMetrics.filter(m => m.custom_metadata?.validation_status === status).length
//           })).sort((a, b) => b.count - a.count);

//           insights.push(`<h3>Data Quality Assessment</h3>`);
//           insights.push(`<p>Data validation status shows <strong>${statusCounts[0].status}</strong> as the most common status with ${statusCounts[0].count} metrics.`);

//           const validatedCount = statusCounts.find(s => s.status === 'validated')?.count || 0;
//           validationRate = Math.round(validatedCount / totalMetrics * 100);

//           insights.push(` Overall, ${validationRate}% of metrics have been validated, indicating ${validationRate >= 70 ? 'strong' : validationRate >= 50 ? 'moderate' : 'room for improvement in'} data quality standards.</p>`);
//         }

//         // Recommendations
//         insights.push(`<h3>Strategic Recommendations</h3>`);
//         insights.push(`<p>Based on the current dataset analysis:</p>`);
//         insights.push(`<ul>`);

//         if (scopes.length < 3) {
//           insights.push(`<li><strong>Expand Scope Coverage:</strong> Consider adding metrics for missing emission scopes to ensure comprehensive ESG reporting.</li>`);
//         }

//         if (validationRate < 80) {
//           insights.push(`<li><strong>Improve Data Validation:</strong> Focus on validating remaining metrics to enhance data reliability and stakeholder confidence.</li>`);
//         }

//         if (categories.length < 5) {
//           insights.push(`<li><strong>Diversify Metric Categories:</strong> Expanding into additional ESG categories can provide more comprehensive sustainability insights.</li>`);
//         }

//         insights.push(`<li><strong>Regular Review:</strong> Establish periodic reviews of metric performance and relevance to ensure continued alignment with organizational goals.</li>`);
//         insights.push(`</ul>`);

//         return insights.join('');
//       };

//       // Create the complete HTML content
//       const htmlContent = `
//         <!DOCTYPE html>
//         <html lang="en">
//           <head>
//             <meta charset="UTF-8">
//             <meta name="viewport" content="width=device-width, initial-scale=1.0">
//             <title>Organization ESG Metrics Report</title>
//             <style>
//               body { 
//                 font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
//                 margin: 20px; 
//                 line-height: 1.6;
//                 color: #333;
//               }
//               .header { 
//                 text-align: center; 
//                 margin-bottom: 30px; 
//                 padding: 20px;
//                 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//                 color: white;
//                 border-radius: 10px;
//               }
//               .header h1 { 
//                 margin: 0; 
//                 font-size: 28px;
//                 font-weight: 600;
//               }
//               .header p { 
//                 margin: 10px 0 0 0; 
//                 opacity: 0.9;
//                 font-size: 14px;
//               }
//               .summary { 
//                 margin-bottom: 30px; 
//                 padding: 20px;
//                 background: #f8f9fa;
//                 border-radius: 8px;
//                 border-left: 4px solid #667eea;
//               }
//               .summary h2 {
//                 margin: 0 0 15px 0;
//                 color: #667eea;
//                 font-size: 20px;
//               }
//               .summary p {
//                 margin: 0;
//                 font-size: 14px;
//               }
//               table { 
//                 width: 100%; 
//                 border-collapse: collapse; 
//                 margin: 20px 0;
//                 font-size: 12px;
//                 box-shadow: 0 2px 8px rgba(0,0,0,0.1);
//                 border-radius: 8px;
//                 overflow: hidden;
//               }
//               th, td { 
//                 border: 1px solid #e0e0e0; 
//                 padding: 12px 8px; 
//                 text-align: left; 
//                 vertical-align: top;
//               }
//               th { 
//                 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//                 color: white;
//                 font-weight: 600;
//                 font-size: 11px;
//                 text-transform: uppercase;
//                 letter-spacing: 0.5px;
//               }
//               tr:nth-child(even) {
//                 background-color: #f8f9fa;
//               }
//               tr:hover {
//                 background-color: #e3f2fd;
//               }
//               .insights {
//                 margin-top: 30px;
//                 padding: 20px;
//                 background: #fff;
//                 border: 1px solid #e0e0e0;
//                 border-radius: 8px;
//               }
//               .insights h3 {
//                 color: #667eea;
//                 margin: 0 0 15px 0;
//                 font-size: 18px;
//                 border-bottom: 2px solid #667eea;
//                 padding-bottom: 5px;
//               }
//               .insights p {
//                 margin: 0 0 15px 0;
//                 text-align: justify;
//               }
//               .insights ul {
//                 margin: 0 0 15px 0;
//                 padding-left: 20px;
//               }
//               .insights li {
//                 margin-bottom: 8px;
//               }
//               .footer {
//                 margin-top: 30px;
//                 text-align: center;
//                 padding: 20px;
//                 background: #f8f9fa;
//                 border-radius: 8px;
//                 font-size: 12px;
//                 color: #666;
//               }
//               .metric-name {
//                 font-weight: 600;
//                 color: #333;
//               }
//               .metric-category {
//                 font-size: 11px;
//                 color: #666;
//                 font-style: italic;
//               }
//               .status-badge {
//                 padding: 4px 8px;
//                 border-radius: 12px;
//                 font-size: 10px;
//                 font-weight: 600;
//                 text-transform: uppercase;
//               }
//               .status-validated { background: #d4edda; color: #155724; }
//               .status-pending { background: #fff3cd; color: #856404; }
//               .status-rejected { background: #f8d7da; color: #721c24; }
//               .scope-badge {
//                 padding: 3px 6px;
//                 border-radius: 8px;
//                 font-size: 10px;
//                 font-weight: 600;
//                 text-transform: uppercase;
//               }
//               .scope-1 { background: #e3f2fd; color: #1976d2; }
//               .scope-2 { background: #f3e5f5; color: #7b1fa2; }
//               .scope-3 { background: #e8f5e8; color: #388e3c; }
//             </style>
//           </head>
//           <body>
//             <div class="header">
//               <h1>Organization ESG Metrics Report</h1>
//               <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
//               <p>Total Metrics: ${filteredMetrics.length} | Filtered Dataset Analysis</p>
//               <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,0.2);">
//                 <p style="margin: 5px 0; font-size: 13px;"><strong>Organization:</strong> ${user?.user_metadata?.organization_name || user?.user_metadata?.full_name || 'N/A'}</p>
//                 <p style="margin: 5px 0; font-size: 13px;"><strong>Generated by:</strong> ${user?.user_metadata?.full_name || user?.email || 'N/A'}</p>
//               </div>
//             </div>
            
//             <div class="summary">
//               <h2>Executive Summary</h2>
//               <p>This comprehensive ESG metrics report provides detailed analysis of your organization's sustainability data. The report includes ${filteredMetrics.length} metrics across various categories and emission scopes, with AI-generated insights to support strategic decision-making.</p>
//             </div>
            
//             <h2 style="color: #667eea; margin: 30px 0 20px 0;">Detailed Metrics Table</h2>
//             <table>
//               <thead>
//                 <tr>
//                   <th>Metric Name</th>
//                   <th>Category</th>
//                   <th>Subcategory</th>
//                   <th>Value</th>
//                   <th>Unit</th>
//                   <th>Scope</th>
//                   <th>Status</th>
//                   <th>Data Source</th>
//                   <th>Reporting Period</th>
//                   <th>Description</th>
//                   <th>Notes</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 ${filteredMetrics.map(metric => `
//                   <tr>
//                     <td>
//                       <div class="metric-name">${metric.name || 'N/A'}</div>
//                     </td>
//                     <td>
//                       <div class="metric-category">${metric.category || 'N/A'}</div>
//                     </td>
//                     <td>${metric.subcategory || 'N/A'}</td>
//                     <td><strong>${metric.custom_value || 'N/A'}</strong></td>
//                     <td>${metric.unit || 'N/A'}</td>
//                     <td>
//                       ${metric.custom_metadata?.scope ?
//           `<span class="scope-badge scope-${metric.custom_metadata.scope.toLowerCase().replace(' ', '-')}">${metric.custom_metadata.scope}</span>` :
//           'N/A'
//         }
//                     </td>
//                     <td>
//                       ${metric.custom_metadata?.validation_status ?
//           `<span class="status-badge status-${metric.custom_metadata.validation_status.toLowerCase()}">${metric.custom_metadata.validation_status}</span>` :
//           'N/A'
//         }
//                     </td>
//                     <td>${metric.custom_metadata?.data_source || 'N/A'}</td>
//                     <td>${metric.custom_metadata?.reporting_period || 'N/A'}</td>
//                     <td>${metric.description || 'N/A'}</td>
//                     <td>${metric.custom_metadata?.notes || 'N/A'}</td>
//                   </tr>
//                 `).join('')}
//               </tbody>
//             </table>
            
//             <div class="insights">
//               <h2 style="color: #667eea; margin: 0 0 20px 0;">AI-Generated Insights & Analysis</h2>
//               ${generateAIInsights()}
//             </div>
            
//             <div class="footer">
//               <p><strong>Report Generated by:</strong> GreenCard Sustainability Manager</p>
//               <p>This report is unique to your current dataset and filter settings. For questions about this analysis, please contact your sustainability team.</p>
//             </div>
//           </body>
//         </html>
//       `;

//       // Create and download the HTML file
//       const blob = new Blob([htmlContent], { type: 'text/html' });
//       const url = URL.createObjectURL(blob);
//       const link = document.createElement('a');
//       link.href = url;
//       link.download = `organization_metrics_report_${new Date().toISOString().split('T')[0]}_${filteredMetrics.length}_metrics.html`;
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);
//       URL.revokeObjectURL(url);

//       toast({
//         title: 'Export Successful',
//         description: `Beautiful HTML report with AI insights has been exported successfully.`,
//       });
//     } catch (error) {
//       console.error('Error exporting HTML:', error);
//       toast({
//         title: 'Export Error',
//         description: 'Failed to export HTML report.',
//         variant: 'destructive'
//       });
//     }
//   };

//   const handleExportPDF = async () => {
//     try {
//       // Generate AI insights based on the current filtered data
//       const generateAIInsights = () => {
//         if (filteredMetrics.length === 0) {
//           return "No metrics data available for analysis.";
//         }

//         const insights = [];

//         // Basic statistics
//         const totalMetrics = filteredMetrics.length;
//         const categories = [...new Set(filteredMetrics.map(m => m.category).filter(Boolean))];
//         const scopes = [...new Set(filteredMetrics.map(m => m.custom_metadata?.scope).filter(Boolean))];
//         const statuses = [...new Set(filteredMetrics.map(m => m.custom_metadata?.validation_status).filter(Boolean))];

//         insights.push(`<h3>Dataset Overview</h3>`);
//         insights.push(`<p>This report contains <strong>${totalMetrics}</strong> ESG metrics across <strong>${categories.length}</strong> categories. The data spans <strong>${scopes.length}</strong> emission scopes with <strong>${statuses.length}</strong> different validation statuses.</p>`);

//         // Category analysis
//         if (categories.length > 0) {
//           const categoryCounts = categories.map(cat => ({
//             category: cat,
//             count: filteredMetrics.filter(m => m.category === cat).length
//           })).sort((a, b) => b.count - a.count);

//           insights.push(`<h3>Category Distribution</h3>`);
//           insights.push(`<p>The most represented category is <strong>${categoryCounts[0].category}</strong> with ${categoryCounts[0].count} metrics (${Math.round(categoryCounts[0].count / totalMetrics * 100)}% of total).`);

//           if (categoryCounts.length > 1) {
//             insights.push(` Other significant categories include <strong>${categoryCounts[1].category}</strong> (${categoryCounts[1].count} metrics) and <strong>${categoryCounts[2]?.category || 'N/A'}</strong> (${categoryCounts[2]?.count || 0} metrics).</p>`);
//           } else {
//             insights.push(`</p>`);
//           }
//         }

//         // Scope analysis
//         if (scopes.length > 0) {
//           const scopeCounts = scopes.map(scope => ({
//             scope: scope,
//             count: filteredMetrics.filter(m => m.custom_metadata?.scope === scope).length
//           })).sort((a, b) => b.count - a.count);

//           insights.push(`<h3>Emission Scope Analysis</h3>`);
//           insights.push(`<p>Scope coverage analysis shows <strong>${scopeCounts[0].scope}</strong> as the most tracked scope with ${scopeCounts[0].count} metrics.`);

//           if (scopeCounts.length > 1) {
//             insights.push(` This is followed by <strong>${scopeCounts[1].scope}</strong> (${scopeCounts[1].count} metrics) and <strong>${scopeCounts[2]?.scope || 'N/A'}</strong> (${scopeCounts[2]?.count || 0} metrics).</p>`);
//           } else {
//             insights.push(`</p>`);
//           }
//         }

//         // Validation status analysis
//         let validationRate = 0; // Declare outside the if block
//         if (statuses.length > 0) {
//           const statusCounts = statuses.map(status => ({
//             status: status,
//             count: filteredMetrics.filter(m => m.custom_metadata?.validation_status === status).length
//           })).sort((a, b) => b.count - a.count);

//           insights.push(`<h3>Data Quality Assessment</h3>`);
//           insights.push(`<p>Data validation status shows <strong>${statusCounts[0].status}</strong> as the most common status with ${statusCounts[0].count} metrics.`);

//           const validatedCount = statusCounts.find(s => s.status === 'validated')?.count || 0;
//           validationRate = Math.round(validatedCount / totalMetrics * 100);

//           insights.push(` Overall, ${validationRate}% of metrics have been validated, indicating ${validationRate >= 70 ? 'strong' : validationRate >= 50 ? 'moderate' : 'room for improvement in'} data quality standards.</p>`);
//         }

//         // Recommendations
//         insights.push(`<h3>Strategic Recommendations</h3>`);
//         insights.push(`<p>Based on the current dataset analysis:</p>`);
//         insights.push(`<ul>`);

//         if (scopes.length < 3) {
//           insights.push(`<li><strong>Expand Scope Coverage:</strong> Consider adding metrics for missing emission scopes to ensure comprehensive ESG reporting.</li>`);
//         }

//         if (validationRate < 80) {
//           insights.push(`<li><strong>Improve Data Validation:</strong> Focus on validating remaining metrics to enhance data reliability and stakeholder confidence.</li>`);
//         }

//         if (categories.length < 5) {
//           insights.push(`<li><strong>Diversify Metric Categories:</strong> Expanding into additional ESG categories can provide more comprehensive sustainability insights.</li>`);
//         }

//         insights.push(`<li><strong>Regular Review:</strong> Establish periodic reviews of metric performance and relevance to ensure continued alignment with organizational goals.</li>`);
//         insights.push(`</ul>`);

//         return insights.join('');
//       };

//       // Create a temporary div element to render the HTML content
//       const tempDiv = document.createElement('div');
//       tempDiv.style.position = 'absolute';
//       tempDiv.style.left = '-9999px';
//       tempDiv.style.top = '0';
//       tempDiv.style.width = '1200px'; // Fixed width for consistent rendering
//       tempDiv.style.backgroundColor = 'white';
//       tempDiv.style.padding = '20px';
//       tempDiv.style.fontFamily = 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif';
//       tempDiv.style.fontSize = '12px';
//       tempDiv.style.lineHeight = '1.6';
//       tempDiv.style.color = '#333';

//       // Create the HTML content with the exact same structure as the original HTML output
//       tempDiv.innerHTML = `
//         <html>
//           <head>
//             <title>Organization ESG Metrics Report</title>
//             <style>
//               body { 
//                 font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
//                 margin: 20px; 
//                 line-height: 1.6;
//                 color: #333;
//               }
//               .header { 
//                 text-align: center; 
//                 margin-bottom: 30px; 
//                 padding: 20px;
//                 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//                 color: white;
//                 border-radius: 10px;
//               }
//               .header h1 { 
//                 margin: 0; 
//                 font-size: 28px;
//                 font-weight: 600;
//               }
//               .header p { 
//                 margin: 10px 0 0 0; 
//                 opacity: 0.9;
//                 font-size: 14px;
//               }
//               .summary { 
//                 margin-bottom: 30px; 
//                 padding: 20px;
//                 background: #f8f9fa;
//                 border-radius: 8px;
//                 border-left: 4px solid #667eea;
//               }
//               .summary h2 {
//                 margin: 0 0 15px 0;
//                 color: #667eea;
//                 font-size: 20px;
//               }
//               .summary p {
//                 margin: 0;
//                 font-size: 14px;
//               }
//               table { 
//                 width: 100%; 
//                 border-collapse: collapse; 
//                 margin: 20px 0;
//                 font-size: 12px;
//                 box-shadow: 0 2px 8px rgba(0,0,0,0.1);
//                 border-radius: 8px;
//                 overflow: hidden;
//               }
//               th, td { 
//                 border: 1px solid #e0e0e0; 
//                 padding: 12px 8px; 
//                 text-align: left; 
//                 vertical-align: top;
//               }
//               th { 
//                 background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//                 color: white;
//                 font-weight: 600;
//                 font-size: 11px;
//                 text-transform: uppercase;
//                 letter-spacing: 0.5px;
//               }
//               tr:nth-child(even) {
//                 background-color: #f8f9fa;
//               }
//               tr:hover {
//                 background-color: #e3f2fd;
//               }
//               .insights {
//                 margin-top: 30px;
//                 padding: 20px;
//                 background: #fff;
//                 border: 1px solid #e0e0e0;
//                 border-radius: 8px;
//               }
//               .insights h3 {
//                 color: #667eea;
//                 margin: 0 0 15px 0;
//                 font-size: 18px;
//                 border-bottom: 2px solid #667eea;
//                 padding-bottom: 5px;
//               }
//               .insights p {
//                 margin: 0 0 15px 0;
//                 text-align: justify;
//               }
//               .insights ul {
//                 margin: 0 0 15px 0;
//                 padding-left: 20px;
//               }
//               .insights li {
//                 margin-bottom: 8px;
//               }
//               .footer {
//                 margin-top: 30px;
//                 text-align: center;
//                 padding: 20px;
//                 background: #f8f9fa;
//                 border-radius: 8px;
//                 font-size: 12px;
//                 color: #666;
//               }
//               .metric-name {
//                 font-weight: 600;
//                 color: #333;
//               }
//               .metric-category {
//                 font-size: 11px;
//                 color: #666;
//                 font-style: italic;
//               }
//               .status-badge {
//                 padding: 4px 8px;
//                 border-radius: 12px;
//                 font-size: 10px;
//                 font-weight: 600;
//                 text-transform: uppercase;
//               }
//               .status-validated { background: #d4edda; color: #155724; }
//               .status-pending { background: #fff3cd; color: #856404; }
//               .status-rejected { background: #f8d7da; color: #721c24; }
//               .scope-badge {
//                 padding: 3px 6px;
//                 border-radius: 8px;
//                 font-size: 10px;
//                 font-weight: 600;
//                 text-transform: uppercase;
//               }
//               .scope-1 { background: #e3f2fd; color: #1976d2; }
//               .scope-2 { background: #f3e5f5; color: #7b1fa2; }
//               .scope-3 { background: #e8f5e8; color: #388e3c; }
//             </style>
//           </head>
//           <body>
//             <div class="header">
//               <h1>Organization ESG Metrics Report</h1>
//               <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
//               <p>Total Metrics: ${filteredMetrics.length} | Filtered Dataset Analysis</p>
//               <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,0.2);">
//                 <p style="margin: 5px 0; font-size: 13px;"><strong>Organization:</strong> ${user?.user_metadata?.organization_name || user?.user_metadata?.full_name || 'N/A'}</p>
//                 <p style="margin: 5px 0; font-size: 13px;"><strong>Generated by:</strong> ${user?.user_metadata?.full_name || user?.email || 'N/A'}</p>
//               </div>
//             </div>
            
//             <div class="summary">
//               <h2>Executive Summary</h2>
//               <p>This comprehensive ESG metrics report provides detailed analysis of your organization's sustainability data. The report includes ${filteredMetrics.length} metrics across various categories and emission scopes, with AI-generated insights to support strategic decision-making.</p>
//             </div>
            
//             <h2 style="color: #667eea; margin: 30px 0 20px 0;">Detailed Metrics Table</h2>
//             <table>
//               <thead>
//                 <tr>
//                   <th>Metric Name</th>
//                   <th>Category</th>
//                   <th>Subcategory</th>
//                   <th>Value</th>
//                   <th>Unit</th>
//                   <th>Scope</th>
//                   <th>Status</th>
//                   <th>Data Source</th>
//                   <th>Reporting Period</th>
//                   <th>Description</th>
//                   <th>Notes</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 ${filteredMetrics.map(metric => `
//                   <tr>
//                     <td>
//                       <div class="metric-name">${metric.name || 'N/A'}</div>
//                     </td>
//                     <td>
//                       <div class="metric-category">${metric.category || 'N/A'}</div>
//                     </td>
//                     <td>${metric.subcategory || 'N/A'}</td>
//                     <td><strong>${metric.custom_value || 'N/A'}</strong></td>
//                     <td>${metric.unit || 'N/A'}</td>
//                     <td>
//                       ${metric.custom_metadata?.scope ?
//           `<span class="scope-badge scope-${metric.custom_metadata.scope.toLowerCase().replace(' ', '-')}">${metric.custom_metadata.scope}</span>` :
//           'N/A'
//         }
//                     </td>
//                     <td>
//                       ${metric.custom_metadata?.validation_status ?
//           `<span class="status-badge status-${metric.custom_metadata.validation_status.toLowerCase()}">${metric.custom_metadata.validation_status}</span>` :
//           'N/A'
//         }
//                     </td>
//                     <td>${metric.custom_metadata?.data_source || 'N/A'}</td>
//                     <td>${metric.custom_metadata?.reporting_period || 'N/A'}</td>
//                     <td>${metric.description || 'N/A'}</td>
//                     <td>${metric.custom_metadata?.notes || 'N/A'}</td>
//                   </tr>
//                 `).join('')}
//               </tbody>
//             </table>
            
//             <div class="insights">
//               <h2 style="color: #667eea; margin: 0 0 20px 0;">AI-Generated Insights & Analysis</h2>
//               ${generateAIInsights()}
//             </div>
            
//             <div class="footer">
//               <p><strong>Report Generated by:</strong> GreenCard Sustainability Manager</p>
//               <p>This report is unique to your current dataset and filter settings. For questions about this analysis, please contact your sustainability team.</p>
//             </div>
//           </body>
//         </html>
//       `;

//       // Add the temporary div to the document
//       document.body.appendChild(tempDiv);

//       // Convert HTML to canvas with better quality settings
//       const canvas = await html2canvas(tempDiv, {
//         scale: 2, // Higher scale for better quality
//         useCORS: true,
//         allowTaint: true,
//         backgroundColor: '#ffffff',
//         width: 1200,
//         height: tempDiv.scrollHeight,
//         logging: false,
//         imageTimeout: 0,
//         removeContainer: true
//       });

//       // Remove the temporary div
//       document.body.removeChild(tempDiv);

//       // Create PDF with proper scaling
//       const imgData = canvas.toDataURL('image/png');
//       const pdf = new jsPDF('p', 'mm', 'a4');
//       const imgWidth = 210; // A4 width in mm
//       const pageHeight = 297; // A4 height in mm
//       const imgHeight = (canvas.height * imgWidth) / canvas.width;
//       let heightLeft = imgHeight;

//       let position = 0;

//       // Add first page
//       pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
//       heightLeft -= pageHeight;

//       // Add additional pages if content is longer than one page
//       while (heightLeft >= 0) {
//         position = heightLeft - imgHeight;
//         pdf.addPage();
//         pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
//         heightLeft -= pageHeight;
//       }

//       // Save the PDF
//       pdf.save(`organization_metrics_report_${new Date().toISOString().split('T')[0]}_${filteredMetrics.length}_metrics.pdf`);

//       toast({
//         title: 'Export Successful',
//         description: `Comprehensive metrics report with AI insights has been exported successfully.`,
//       });
//     } catch (error) {
//       console.error('Error exporting PDF:', error);
//       toast({
//         title: 'Export Error',
//         description: 'Failed to export metrics report.',
//         variant: 'destructive'
//       });
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-64">
//         <div className="flex items-center gap-2">
//           <RefreshCw className="h-4 w-4 animate-spin" />
//           <span>Loading organization metrics...</span>
//         </div>
//       </div>
//     );
//   }

//   if (userMetrics.length === 0) {
//     return (
//       <div className="space-y-6">
//         <Card>
//           <CardHeader>
//             <CardTitle className="flex items-center gap-2">
//               <BarChart3 className="h-5 w-5" />
//               Organization ESG Metrics
//             </CardTitle>
//           </CardHeader>
//           <CardContent>
//             <Alert>
//               <AlertDescription className="text-center py-8">
//                 <div className="flex flex-col items-center gap-4">
//                   <BarChart3 className="h-12 w-12 text-gray-400" />
//                   <div>
//                     <h3 className="text-lg font-medium text-gray-900 mb-2">
//                       No ESG Metrics Added Yet
//                     </h3>
//                     <p className="text-gray-600 mb-4">
//                       Start by adding metrics through the Quick Entry tab or by importing from the Metrics Library.
//                     </p>
//                     <div className="flex gap-2 justify-center">
//                       <Button onClick={() => onTabChange?.('quick-entry')}>
//                         <Upload className="h-4 w-4 mr-2" />
//                         Add Data
//                       </Button>
//                       <Button variant="outline" onClick={() => onTabChange?.('metrics')}>
//                         <Library className="h-4 w-4 mr-2" />
//                         Browse Metrics
//                       </Button>
//                     </div>
//                   </div>
//                 </div>
//               </AlertDescription>
//             </Alert>
//           </CardContent>
//         </Card>
//       </div>
//     );
//   }

//   return (
//     <div className="space-y-6">
//       <Card>
//         <CardHeader>
//           <CardTitle className="flex items-center justify-between">
//             <span>Organization ESG Metrics ({filteredMetrics.length} items)</span>
//             <div className="flex gap-2">
//               <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={loading}>
//                 <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
//                 Refresh
//               </Button>
//               <Button variant="outline" size="sm" onClick={handleExportCSV}>
//                 <Download className="w-4 h-4 mr-2" />
//                 Export CSV
//               </Button>
//               <Button variant="outline" size="sm" onClick={handleExportHTML}>
//                 <FileText className="w-4 h-4 mr-2" />
//                 Export HTML
//               </Button>
//               <Button variant="outline" size="sm" onClick={handleExportPDF}>
//                 <FileText className="w-4 h-4 mr-2" />
//                 Export PDF
//               </Button>
//             </div>
//           </CardTitle>
//           <div className="flex flex-col sm:flex-row gap-4">
//             <div className="flex-1">
//               <div className="relative">
//                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
//                 <Input
//                   placeholder="Search metrics..."
//                   value={searchTerm}
//                   onChange={handleSearchChange}
//                   className="pl-10"
//                 />
//               </div>
//             </div>
//             <div className="flex gap-2">
//               <Select value={filterCategory} onValueChange={setFilterCategory}>
//                 <SelectTrigger className="w-[140px]">
//                   <SelectValue placeholder="Category" />
//                 </SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="all">All Categories</SelectItem>
//                   {categories.map(category => (
//                     <SelectItem key={category} value={category}>{category}</SelectItem>
//                   ))}
//                 </SelectContent>
//               </Select>
//               <Select value={filterScope} onValueChange={setFilterScope}>
//                 <SelectTrigger className="w-[120px]">
//                   <SelectValue placeholder="Scope" />
//                 </SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="all">All Scopes</SelectItem>
//                   <SelectItem value="Scope 1">Scope 1</SelectItem>
//                   <SelectItem value="Scope 2">Scope 2</SelectItem>
//                   <SelectItem value="Scope 3">Scope 3</SelectItem>
//                 </SelectContent>
//               </Select>
//               <Button variant="outline" size="sm" onClick={clearFilters}>
//                 <Filter className="h-4 w-4" />
//               </Button>
//             </div>
//           </div>
//         </CardHeader>
//         <CardContent>
//           <div className="rounded-md border">
//             <Table>
//               <TableHeader>
//                 <TableRow>
//                   <TableHead>Metric Name</TableHead>
//                   <TableHead>Category</TableHead>
//                   <TableHead>Value</TableHead>
//                   <TableHead>Unit</TableHead>
//                   <TableHead>Scope</TableHead>
//                   <TableHead>Status</TableHead>
//                   <TableHead>Actions</TableHead>
//                 </TableRow>
//               </TableHeader>
//               <TableBody>
//                 {filteredMetrics.map((metric) => (
//                   <TableRow key={metric.id}>
//                     <TableCell>
//                       <div>
//                         <div className="font-medium">{metric.name || 'N/A'}</div>
//                         <div className="text-sm text-gray-500">{metric.subcategory || 'N/A'}</div>
//                       </div>
//                     </TableCell>
//                     <TableCell>
//                       <div className="flex items-center gap-2">
//                         {getCategoryIcon(metric.category)}
//                         <Badge variant="outline">{metric.category || 'N/A'}</Badge>
//                       </div>
//                     </TableCell>
//                     <TableCell>{metric.custom_value || 'N/A'}</TableCell>
//                     <TableCell>{metric.unit || 'N/A'}</TableCell>
//                     <TableCell>
//                       {metric.custom_metadata?.scope ? (
//                         <Badge className={getScopeColor(metric.custom_metadata.scope)}>
//                           {metric.custom_metadata.scope}
//                         </Badge>
//                       ) : (
//                         <span className="text-gray-400 text-sm">N/A</span>
//                       )}
//                     </TableCell>
//                     <TableCell>
//                       {metric.custom_metadata?.validation_status ? (
//                         <Badge className={getStatusColor(metric.custom_metadata.validation_status)}>
//                           {metric.custom_metadata.validation_status}
//                         </Badge>
//                       ) : (
//                         <span className="text-gray-400 text-sm">N/A</span>
//                       )}
//                     </TableCell>
//                     <TableCell>
//                       <div className="flex gap-2">
//                         <Button
//                           variant="ghost"
//                           size="sm"
//                           onClick={() => handleView(metric)}
//                         >
//                           <Eye className="h-4 w-4" />
//                         </Button>
//                         <Button
//                           variant="ghost"
//                           size="sm"
//                           onClick={() => handleEdit(metric)}
//                         >
//                           <Edit className="h-4 w-4" />
//                         </Button>
//                         <Button
//                           variant="ghost"
//                           size="sm"
//                           onClick={() => handleDelete(metric)}
//                         >
//                           <Trash2 className="h-4 w-4" />
//                         </Button>
//                       </div>
//                     </TableCell>
//                   </TableRow>
//                 ))}
//               </TableBody>
//             </Table>
//           </div>
//         </CardContent>
//       </Card>

//       {/* Edit Modal - This remains the same */}
//       <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Edit Metric</DialogTitle>
//             <DialogDescription>
//               Update the metric values and metadata.
//             </DialogDescription>
//           </DialogHeader>
//           <div className="space-y-4">
//             <div>
//               <Label htmlFor="custom_value">Value</Label>
//               <Input
//                 id="custom_value"
//                 type="number"
//                 step="0.01"
//                 value={editFormData.custom_value}
//                 onChange={(e) => setEditFormData(prev => ({ ...prev, custom_value: e.target.value }))}
//                 placeholder="Enter metric value"
//               />
//             </div>
//             <div>
//               <Label htmlFor="unit">Unit</Label>
//               <Input
//                 id="unit"
//                 value={editFormData.unit}
//                 onChange={(e) => setEditFormData(prev => ({ ...prev, unit: e.target.value }))}
//                 placeholder="e.g., kg CO2e, kWh, etc."
//               />
//             </div>
//             <div>
//               <Label htmlFor="notes">Notes</Label>
//               <Textarea
//                 id="notes"
//                 value={editFormData.notes}
//                 onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
//                 placeholder="Additional notes or context"
//                 rows={3}
//               />
//             </div>

//             {/* Tags Section */}
//             <div>
//               <Label className="text-sm font-medium">Tags</Label>
//               <p className="text-xs text-gray-600 mb-2">
//                 Manage tags to organize and categorize this metric for better analysis and filtering
//               </p>
//               <DataTaggingComponent
//                 selectedTags={editSelectedTags}
//                 onTagsChange={setEditSelectedTags}
//                 dataType="metric"
//                 showCreateTag={true}
//               />
//             </div>

//             <div className="flex justify-end gap-2">
//               <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
//                 Cancel
//               </Button>
//               <Button onClick={handleSaveEdit}>
//                 Save Changes
//               </Button>
//             </div>
//           </div>
//         </DialogContent>
//       </Dialog>

//       {/* View Modal - This remains the same */}
//       <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
//         <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
//           <DialogHeader>
//             <DialogTitle className="flex items-center gap-2">
//               {selectedMetric && getCategoryIcon(selectedMetric.category)}
//               {selectedMetric?.name || 'Metric Details'}
//             </DialogTitle>
//             <DialogDescription>
//               Comprehensive view of all metric information and metadata
//             </DialogDescription>
//           </DialogHeader>
//           {selectedMetric && (
//             <div className="space-y-6">
//               {/* Basic Information Section */}
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h3 className="text-lg font-semibold mb-3 text-gray-800">Basic Information</h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Metric Name</Label>
//                     <p className="text-sm font-medium text-gray-900">{selectedMetric.name || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Category</Label>
//                     <div className="flex items-center gap-2">
//                       {getCategoryIcon(selectedMetric.category)}
//                       <Badge variant="outline">{selectedMetric.category || 'N/A'}</Badge>
//                     </div>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Subcategory</Label>
//                     <p className="text-sm text-gray-900">{selectedMetric.subcategory || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Value</Label>
//                     <p className="text-sm font-semibold text-gray-900">
//                       {selectedMetric.custom_value || 'N/A'} {selectedMetric.unit || ''}
//                     </p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Unit</Label>
//                     <p className="text-sm text-gray-900">{selectedMetric.unit || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Description</Label>
//                     <p className="text-sm text-gray-900">{selectedMetric.description || 'N/A'}</p>
//                   </div>
//                 </div>
//               </div>

//               {/* ESG Classification Section */}
//               <div className="bg-blue-50 p-4 rounded-lg">
//                 <h3 className="text-lg font-semibold mb-3 text-blue-800">ESG Classification</h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
//                   <div>
//                     <Label className="text-sm font-medium text-blue-700">Emission Scope</Label>
//                     {selectedMetric.custom_metadata?.scope ? (
//                       <Badge className={getScopeColor(selectedMetric.custom_metadata.scope)}>
//                         {selectedMetric.custom_metadata.scope}
//                       </Badge>
//                     ) : (
//                       <p className="text-sm text-gray-500">N/A</p>
//                     )}
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-blue-700">Validation Status</Label>
//                     {selectedMetric.custom_metadata?.validation_status ? (
//                       <Badge className={getStatusColor(selectedMetric.custom_metadata.validation_status)}>
//                         {selectedMetric.custom_metadata.validation_status}
//                       </Badge>
//                     ) : (
//                       <p className="text-sm text-gray-500">N/A</p>
//                     )}
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-blue-700">Data Source</Label>
//                     <p className="text-sm text-gray-900">{selectedMetric.custom_metadata?.data_source || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-blue-700">Reporting Period</Label>
//                     <p className="text-sm text-gray-900">{selectedMetric.custom_metadata?.reporting_period || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-blue-700">Calculation Method</Label>
//                     <p className="text-sm text-gray-900">{selectedMetric.custom_metadata?.calculation_method || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-blue-700">Verification Status</Label>
//                     <p className="text-sm text-gray-900">{selectedMetric.custom_metadata?.verification_status || 'N/A'}</p>
//                   </div>
//                 </div>
//               </div>

//               {/* Additional Metadata Section */}
//               <div className="bg-green-50 p-4 rounded-lg">
//                 <h3 className="text-lg font-semibold mb-3 text-green-800">Additional Information</h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div>
//                     <Label className="text-sm font-medium text-green-700">Notes</Label>
//                     <p className="text-sm text-gray-900 whitespace-pre-wrap">
//                       {selectedMetric.custom_metadata?.notes || 'No additional notes available'}
//                     </p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-green-700">Tags</Label>
//                     <div className="flex flex-wrap gap-1">
//                       {viewSelectedTags && viewSelectedTags.length > 0 ? (
//                         viewSelectedTags.map((tagId, index) => (
//                           <Badge key={index} variant="secondary" className="text-xs">
//                             {tagId}
//                           </Badge>
//                         ))
//                       ) : (
//                         <p className="text-sm text-gray-500">No tags assigned</p>
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* System Information Section */}
//               <div className="bg-gray-100 p-4 rounded-lg">
//                 <h3 className="text-lg font-semibold mb-3 text-gray-800">System Information</h3>
//                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Metric ID</Label>
//                     <p className="text-sm text-gray-900 font-mono">{selectedMetric.id || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Library ID</Label>
//                     <p className="text-sm text-gray-900 font-mono">{selectedMetric.metric_id || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">User ID</Label>
//                     <p className="text-sm text-gray-900 font-mono">{selectedMetric.organization_id || 'N/A'}</p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Created At</Label>
//                     <p className="text-sm text-gray-900">
//                       {selectedMetric.created_at ? new Date(selectedMetric.created_at).toLocaleString() : 'N/A'}
//                     </p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Updated At</Label>
//                     <p className="text-sm text-gray-900">
//                       {selectedMetric.updated_at ? new Date(selectedMetric.updated_at).toLocaleString() : 'N/A'}
//                     </p>
//                   </div>
//                   <div>
//                     <Label className="text-sm font-medium text-gray-700">Sync Status</Label>
//                     <Badge variant={selectedMetric.custom_metadata?.sync_status === 'synced' ? 'default' : 'secondary'}>
//                       {selectedMetric.custom_metadata?.sync_status || 'synced'}
//                     </Badge>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
//         </DialogContent>
//       </Dialog>

//       {/* Delete Confirmation Dialog */}
//       <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
//         <DialogContent>
//           <DialogHeader>
//             <DialogTitle>Confirm Deletion</DialogTitle>
//             <DialogDescription>
//               Are you sure you want to delete "{metricToDelete?.name}" from your metrics? This action cannot be undone.
//             </DialogDescription>
//           </DialogHeader>
//           <div className="flex justify-end gap-2 mt-4">
//             <Button
//               variant="outline"
//               onClick={() => {
//                 setIsDeleteDialogOpen(false);
//                 setMetricToDelete(null);
//               }}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant="destructive"
//               onClick={confirmDelete}
//             >
//               Delete
//             </Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//     </div>
//   );
// };
